import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Form, Modal, Row, Space, Typography } from 'antd';
import { useDispatch } from 'react-redux';

import { PageWrapper } from '@/components';
import { useNotification } from '@/hooks';

import { SaveOutlined, CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';

import styles from './index.module.scss';
import classNames from "classnames";
import { log } from '@craco/craco/lib/logger';

const SavePageContainer = ({
                               form: SaveForm,
                               isCreating,
                               objectName,
                               actionButtons,
                               disableActions,
                               listUrl,
                               detailUrl,
                               tabs: tabsProp,
                               createAction,
                               updateAction,
                               getDetailAction,
                               onCreate: onCreateProp,
                               onUpdate: onUpdateProp,
                               getBreadcrumbs,
                               getTabs,
                               onChangeTab,
                               getDetailDataMapping,
                               pageTitle,
                               isRefreshUpdateSuccess = false,
                           }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [detailData, setDetailData] = useState({});
    const [errorData, setErrorData] = useState({});
    const [modalProps, setModalProps] = useState({isOpen: false, isSuccess: true});
    const {id} = useParams();
    const [form] = Form.useForm();

    const {showErrorMessage} = useNotification();

    const formId = `form-${objectName}`;
    const breadcrumbs = getBreadcrumbs?.(detailData) || [];
    const tabs = getTabs?.(detailData) || tabsProp;

    const onCancel = () => {
        if (listUrl) {
            navigate(listUrl);
        }
    }

    const onCreate = (values) => {
        if (onCreateProp) {
            onCreateProp(values);
        } else if (createAction) {
            setIsSubmitting(true);
            dispatch(createAction({
                params: values,
                onCompleted: response => {
                    if (response?.result) {
                        onSaveSuccess(response.data);
                    } else {
                        onSaveFail(response);
                    }
                },
                onError: err => {
                    onSaveFail(err);
                }
            }))
        }
    }

    const onUpdate = (values) => {
        if (onUpdateProp) {
            onUpdateProp(values);
        } else if (updateAction) {
            setIsSubmitting(true);
            dispatch(updateAction({
                params: {id: detailData.id, ...values},
                onCompleted: response => {
                    if (response?.result) {
                        onSaveSuccess(response.data);
                    } else {
                        onSaveFail(response);
                    }
                },
                onError: err => {
                    onSaveFail(err);
                }
            }))
        }
    }

    const onSaveFail = (err) => {
        setErrorData(err);
        setIsSubmitting(false);
        setModalProps({isOpen: true, isSuccess: false});
    }

    const onSaveSuccess = (data) => {
        setIsSubmitting(false);
        console.log({data});

        if (isRefreshUpdateSuccess) {
            setDetailData(data);
        }
        setModalProps({isOpen: true, isSuccess: true});
    }

    const onGetDetailFail = useCallback((err) => {
        showErrorMessage(`Get data failed. Please try again!`);
        navigate(listUrl);
    }, [navigate, showErrorMessage, listUrl])

    useEffect(() => {
        if (!isCreating && id && getDetailAction) {
            dispatch(getDetailAction({
                params: {id},
                onCompleted: response => {
                    if (response?.result) {
                        const data = getDetailDataMapping ? getDetailDataMapping(response.data) : response.data;
                        setDetailData(data);
                    } else {
                        onGetDetailFail(response);
                    }
                },
                onError: err => {
                    onGetDetailFail(err);
                }
            }))
        }
    }, [isCreating, id])

    const onContinueEdit = () => {
        setModalProps({...modalProps, isOpen: false})
        if (isCreating) {
            navigate(detailUrl.replace(':id', detailData.id))
        }
    }

    useEffect(() => {
        form.setFieldsValue(detailData);
    }, [form, detailData])

    return (
        <PageWrapper
            breadcrumbs={breadcrumbs}
            tabs={tabs}
            onChangeTab={onChangeTab}
        >
            <div className={styles.savePage}>
                <div className={classNames(styles.savePageForm, {
                    [styles.hasTabs]: tabs,
                    [styles.disableAction]: disableActions,
                    [styles.hasTabsDisableAction]: disableActions && tabs
                })}>
                    <Card style={{borderRadius: 0, minHeight: '100%'}}>
                        <h5 className={styles.pageTitle}>{pageTitle}</h5>
                        <SaveForm
                            form={form}
                            formId={formId}
                            detailData={detailData}
                            isCreating={isCreating}
                            setDetailData={setDetailData}
                            onSubmit={isCreating ? onCreate : onUpdate}
                        />
                    </Card>
                </div>
                {
                    !disableActions
                        ?
                        <div className={styles.actionBar}>
                            {
                                actionButtons || (
                                    <>
                                        <Button disabled={isSubmitting} onClick={onCancel}>Hủy</Button>
                                        <Button form={formId} type="primary" htmlType="submit" loading={isSubmitting}
                                                icon={<SaveOutlined/>}>
                                            {isCreating ? 'Lưu' : 'Lưu & Cập nhật'}
                                        </Button>
                                    </>
                                )
                            }
                        </div>
                        :
                        null
                }
            </div>
            <Modal open={modalProps.isOpen}
                   closable={false}
                   centered={true}
                   footer={[]}
            >
                <Row justify="center">
                    <Space direction="vertical" align="center" size="large">
                        {
                            modalProps.isSuccess ?
                                <>
                                    <CheckCircleFilled style={{fontSize: 70, color: '#52C41A'}}/>
                                    <Typography.Title level={3} style={{textTransform: "capitalize"}}>
                                        {`${isCreating ? 'Tạo' : 'Cập nhật'} ${objectName} thành công!`}
                                    </Typography.Title>
                                    <Space>
                                        <Button onClick={onContinueEdit}>
                                            Tiếp tục chỉnh sửa {objectName}
                                        </Button>
                                        <Button type="primary" onClick={onCancel}>Quay lại danh sách</Button>
                                    </Space>
                                </>
                                :
                                <>
                                    <CloseCircleFilled style={{fontSize: 70, color: '#EB5757'}}/>
                                    <Typography.Title level={3} style={{textTransform: "capitalize"}}>
                                        {`${isCreating ? 'Tạo' : 'Cập nhật'} ${objectName} thất bại.`}
                                    </Typography.Title>
                                    <Typography.Text style={{color: "#EB5757"}}>
                                        {errorData.message}
                                    </Typography.Text>
                                    <Space>
                                        <Button onClick={() => setModalProps({...modalProps, isOpen: false})}>
                                            {`Tiếp tục ${isCreating ? 'tạo' : 'cập nhật'}`}
                                        </Button>
                                        <Button type="primary" onClick={onCancel}>Quay lại danh sách</Button>
                                    </Space>
                                </>
                        }
                    </Space>
                </Row>
            </Modal>
        </PageWrapper>

    )
}

export default SavePageContainer;